// Geographic helpers: slippy-map tile math, bounding boxes, grid <-> lat/lon mapping.

export const EARTH_RADIUS_M = 6378137;

// Meters per degree at a given latitude
export const metersPerDegLat = () => 111320;
export const metersPerDegLon = (latDeg) =>
  111320 * Math.cos((latDeg * Math.PI) / 180);

// Bounding box { south, west, north, east } from a center point and radius in km
export const bboxFromCenter = (lat, lon, radiusKm) => {
  const dLat = (radiusKm * 1000) / metersPerDegLat();
  const dLon = (radiusKm * 1000) / metersPerDegLon(lat);
  return {
    south: lat - dLat,
    north: lat + dLat,
    west: lon - dLon,
    east: lon + dLon,
  };
};

// Web-mercator tile coordinates for a lat/lon at zoom z
export const latLonToTile = (lat, lon, z) => {
  const latRad = (lat * Math.PI) / 180;
  const n = Math.pow(2, z);
  const x = ((lon + 180) / 360) * n;
  const y =
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n;
  return { x, y };
};

// Fractional pixel position of a lat/lon within the world at zoom z (256px tiles)
export const latLonToWorldPx = (lat, lon, z) => {
  const t = latLonToTile(lat, lon, z);
  return { px: t.x * 256, py: t.y * 256 };
};

// Grid definition for a bbox: fixed number of columns, rows derived so cells
// are approximately square in meters.
export const gridFromBbox = (bbox, cols) => {
  const midLat = (bbox.south + bbox.north) / 2;
  const widthM = (bbox.east - bbox.west) * metersPerDegLon(midLat);
  const heightM = (bbox.north - bbox.south) * metersPerDegLat();
  const cellSizeM = widthM / cols;
  const rows = Math.max(2, Math.round(heightM / cellSizeM));
  return { rows, cols, cellSizeM, widthM, heightM };
};

// Lat/lon of the center of grid cell (r, c). Row 0 is the NORTH edge.
export const cellCenterLatLon = (bbox, dims, r, c) => {
  const lat =
    bbox.north - ((r + 0.5) / dims.rows) * (bbox.north - bbox.south);
  const lon = bbox.west + ((c + 0.5) / dims.cols) * (bbox.east - bbox.west);
  return { lat, lon };
};

// Grid cell containing a lat/lon (may be out of bounds)
export const latLonToCell = (bbox, dims, lat, lon) => {
  const r = Math.floor(((bbox.north - lat) / (bbox.north - bbox.south)) * dims.rows);
  const c = Math.floor(((lon - bbox.west) / (bbox.east - bbox.west)) * dims.cols);
  return { row: r, col: c };
};

export const isInBounds = (r, c, rows, cols) =>
  r >= 0 && r < rows && c >= 0 && c < cols;

// Ray-casting point-in-polygon. ring: [{lat, lon}, ...]
export const pointInRing = (lat, lon, ring) => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const yi = ring[i].lat,
      xi = ring[i].lon;
    const yj = ring[j].lat,
      xj = ring[j].lon;
    const intersect =
      (yi > lat) !== (yj > lat) &&
      lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

// Quick bbox for a ring so we can skip point-in-polygon tests cheaply
export const ringBbox = (ring) => {
  let south = Infinity,
    north = -Infinity,
    west = Infinity,
    east = -Infinity;
  for (const p of ring) {
    if (p.lat < south) south = p.lat;
    if (p.lat > north) north = p.lat;
    if (p.lon < west) west = p.lon;
    if (p.lon > east) east = p.lon;
  }
  return { south, north, west, east };
};
